import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import store from 'shared/store'
import { type IState } from 'types/redux'
import {
  currencyToSymbol,
  formatPriceString,
} from 'views/components/atoms/Price/helpers'
import {
  MINIMUM_VALUE_NOT_REACHED,
  NO_AVAILABLE_SLOT,
  NOT_IN_DELIVERY_REGION,
  PRODUCTS_NOT_AVAILABLE,
  RX_NOT_AVAILABLE,
} from 'views/consts/notEligibleReasons'
import {
  getSameDaySlotsSortedBySecondsLeft,
  setTimeleft,
} from 'views/utils/nowCountdownTimer'

export const useNowWithDeliveryTimeCaseBox = () => {
  const nowData = useSelector((state: IState) => state.nowData)

  const hasNowDataSlots = nowData?.slots?.length
  const slot = hasNowDataSlots ? nowData.slots?.[0] : null
  const deliveryDay = slot?.deliveryDay
  const deliveryTime = slot?.deliveryTimeInfo ? slot?.deliveryTimeInfo : ''

  const secondsLeft = hasNowDataSlots
    ? getSameDaySlotsSortedBySecondsLeft(nowData?.slots)?.[0]?.secondsLeft ?? 0
    : 0

  const isNowEligible = nowData?.nowEligible
  const hasNotEligibleReason =
    nowData?.notEligibleReason === PRODUCTS_NOT_AVAILABLE ||
    nowData?.notEligibleReason === RX_NOT_AVAILABLE
  const hasNotInDeliveryRegion =
    nowData?.notEligibleReason === NOT_IN_DELIVERY_REGION
  const hasMinimumValueNotReached =
    nowData?.notEligibleReason === MINIMUM_VALUE_NOT_REACHED
  const hasNoAvaliableSlot = nowData?.notEligibleReason === NO_AVAILABLE_SLOT

  const {
    publicConfig: { locale, currencySymbolRight },
  } = store.getPublicRuntimeConfig()

  const [time, setTime] = useState('')
  const [countdownTime, setCountdownTime] = useState(secondsLeft.toString())

  const nowCentsDifferenceMinimumOrderValue =
    nowData?.centsDifferenceMinimumOrderValue
  const currencySymbol = currencyToSymbol('EUR')
  const differenceMinimumOrderValue = nowCentsDifferenceMinimumOrderValue
    ? nowCentsDifferenceMinimumOrderValue / 100
    : 0
  const diffPrice = formatPriceString({
    amount: differenceMinimumOrderValue,
    locale,
    currencySymbol,
    symbolRight: currencySymbolRight,
  })

  useEffect(() => {
    const interval = setTimeleft(countdownTime, setTime, setCountdownTime)

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [countdownTime, nowData])

  useEffect(() => {
    setCountdownTime(secondsLeft?.toString())
  }, [secondsLeft])

  let dataQAid: string

  if (isNowEligible) {
    dataQAid = 'is-now-eligible'
  } else if (hasNotEligibleReason) {
    dataQAid = 'has-not-in-delivery-region'
  } else if (hasNotInDeliveryRegion) {
    dataQAid = 'has-not-eligible-reason'
  } else if (hasMinimumValueNotReached) {
    dataQAid = 'has-minimum-value-not-reached'
  } else if (hasNoAvaliableSlot) {
    dataQAid = 'has-no-avaliable-slot'
  } else {
    dataQAid = 'default'
  }

  return {
    deliveryDay,
    deliveryTime,
    timeLeft: time,
    isNowEligible,
    hasNotEligibleReason,
    hasNotInDeliveryRegion,
    hasMinimumValueNotReached,
    hasNoAvaliableSlot,
    dataQAid,
    nowCentsDifferenceMinimumOrderValue,
    diffPrice,
  }
}
